
import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { AuthContextType } from '@/types/auth';
import { useAuthState } from './auth/useAuthState';
import { authService } from './auth/authService';
import { AppRole } from '@/types/auth';
import { supabase } from '@/integrations/supabase/client';
import { permissionsManager } from '@/utils/permissions';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, session, loading, loadingMessage, refreshUserData } = useAuthState();

  // Load permissions when user logs in
  useEffect(() => {
    if (user) {
      permissionsManager.load().catch(err => 
        console.error('[Auth] Failed to load permissions:', err)
      );
    }
  }, [user?.id]);

  const signIn = async (email: string, password: string) => {
    return await authService.signIn(email, password);
  };

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string, organizationName?: string, invitationId?: string) => {
    return await authService.signUp(email, password, firstName, lastName, organizationName, invitationId);
  };

  const signOut = async () => {
    await authService.signOut();
  };

  const resetPassword = async (email: string) => {
    return await authService.resetPassword(email);
  };

  // Memoize role checks for performance
  // @deprecated Use usePermissions hook with resource-action checks instead
  // Example: const { can } = usePermissions(); if (can('recipes', 'create')) { ... }
  // These methods remain for backward compatibility and route-level role checks
  const hasRole = useMemo(() => (role: AppRole): boolean => {
    return user?.roles.includes(role) || false;
  }, [user?.roles]);

  // @deprecated Use usePermissions hook with resource-action checks instead
  // Example: const { can } = usePermissions(); if (can('users', 'manage')) { ... }
  const hasAnyRole = useMemo(() => (roles: AppRole[]): boolean => {
    return roles.some(role => user?.roles.includes(role)) || false;
  }, [user?.roles]);

  // @deprecated Use usePermissions hook with resource-action checks instead
  // This method remains for route-level access control only
  const canAccess = useMemo(() => (requiredRoles: AppRole[]): boolean => {
    if (!user) return false;
    return requiredRoles.some(role => user.roles.includes(role));
  }, [user?.roles]);

  // Real-time role verification - instant updates via Supabase Realtime
  useEffect(() => {
    if (!user) return;
    
    console.log('[Auth] Setting up real-time subscriptions for user:', user.id);
    
    // Create a channel for this user's auth changes
    const channel = supabase
      .channel(`auth-changes:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_roles',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('[Auth] Real-time: user_roles changed', payload);
          refreshUserData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          console.log('[Auth] Real-time: profile updated', payload);
          refreshUserData();
        }
      )
      .subscribe((status) => {
        console.log('[Auth] Real-time subscription status:', status);
      });
    
    return () => {
      console.log('[Auth] Cleaning up real-time subscriptions');
      supabase.removeChannel(channel);
    };
  }, [user?.id, refreshUserData]);

  const refreshSession = async () => {
    const { data: { session }, error } = await supabase.auth.refreshSession();
    if (error) {
      throw error;
    }
    return session;
  };

  const updateOnboardingStatus = async (step: string, completed: boolean = false) => {
    if (!user) {
      return;
    }

    try {
      const currentProgress = user.profile?.onboarding_progress || {
        completed: false,
        current_step: 1,
        steps_completed: {},
        skipped: false
      };

      const updates = {
        onboarding_progress: {
          ...currentProgress,
          current_step: completed ? currentProgress.current_step : step,
          completed,
          steps_completed: {
            ...currentProgress.steps_completed,
            [step]: true
          },
          completed_at: completed ? new Date().toISOString() : currentProgress.completed_at
        },
        is_setup_complete: completed
      };

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      // Refresh user data to get updated profile
      await refreshUserData();
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    loadingMessage,
    signIn,
    signUp,
    signOut,
    resetPassword,
    hasRole,
    hasAnyRole,
    canAccess,
    refreshUserData,
    refreshSession,
    updateOnboardingStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
